import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import Modal from '.';
import { key } from 'styled-theme';

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 500;
  color: #111827;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  color: #f35151;
  transition: color 0.2s;

  &:hover {
    color: #f30808;
    background-color: #e0e0e0;
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const ModalBody = styled.div`
  padding: 16px 24px;
`;

const ContentWrapper = styled.div`
  margin-bottom: 24px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding-top: 10px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
  font-family: ${key('fonts.primary', 'JetBrains Mono Regular')};

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  border: 1px solid #d1d5db;
  padding: 12px 30px;
  font-size: 16px;
  border-radius: ${key('sizes.borderRadius.normal', '18px')};

  &:hover:not(:disabled) {
    background-color: #f3f4f6;
  }
`;

const ConfirmButton = styled(Button)`
  background-color: #2563eb;
  border: 1px solid #2563eb;
  color: #fff;
  padding: 12px 30px;
  font-size: 16px;
  border-radius: ${key('sizes.borderRadius.normal', '18px')};

  &:hover:not(:disabled) {
    background-color: #1d4ed8;
  }
`;

const ConfirmationModal = ({
  isModalOpen,
  onClose,
  onConfirm,
  title = 'Confirmation',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Yes',
  cancelText = 'Cancel',
  isLoading = false,
}) => {
  const closeButtonRef = useRef(null);
  const confirmButtonRef = useRef(null);
  const modalRef = useRef(null);
  const lastFocusedElement = useRef(null);

  // Handle focus management for accessibility
  useEffect(() => {
    if (isModalOpen) {
      lastFocusedElement.current = document.activeElement;
      setTimeout(() => confirmButtonRef.current?.focus(), 100);
    } else if (lastFocusedElement.current) {
      lastFocusedElement.current.focus();
    }
  }, [isModalOpen]);

  const handleKeyDown = (event) => {
    if (!isModalOpen) return;

    if (event.key === 'Escape') {
      onClose();
    }

    if (event.key === 'Tab') {
      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }
  };

  if (!isModalOpen) return null;

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={onClose}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirmation-title"
      aria-describedby="confirmation-message"
      onKeyDown={handleKeyDown}
      ref={modalRef}
    >
      <ModalHeader>
        <ModalTitle id="confirmation-title">{title}</ModalTitle>
        <CloseButton
          onClick={onClose}
          ref={closeButtonRef}
          aria-label="Close confirmation dialog"
          type="button"
        >
          <X size={24} aria-hidden="true" />
        </CloseButton>
      </ModalHeader>

      <ModalBody>
        <ContentWrapper id="confirmation-message">
          {typeof message === 'string' ? <p>{message}</p> : message}
        </ContentWrapper>

        <ButtonGroup>
          <CancelButton type="button" onClick={onClose} disabled={isLoading}>
            {cancelText}
          </CancelButton>
          <ConfirmButton
            type="button"
            onClick={onConfirm}
            ref={confirmButtonRef}
            disabled={isLoading}
          >
            {confirmText}
          </ConfirmButton>
        </ButtonGroup>
      </ModalBody>
    </Modal>
  );
};

export default ConfirmationModal;
